import { adobeCleanString, fireAdobeAnalyticsEvent } from './adobe-analytics';

const VIDEO_MILESTONES = [25, 50, 75];
// Players tick at least once a second while playing; a longer gap is a seek.
const VIDEO_MAX_TICK_SECONDS = 2;

// Launch's Video rules listen for analytics:video:* events. Milestones
// send once per viewing; playing again after the end starts a new one.
// Players feed it play/timeUpdate/pause/complete; getVideo returns
// { category, id, title } for eVar70, Category|ID|Name.
export class VideoAnalytics {
  constructor(getVideo) {
    this.getVideo = getVideo;
    this.started = false;
    this.ended = false;
    this.milestones = new Set();
    this.unreportedSeconds = 0;
    this.lastSeconds = null;
  }

  play() {
    if (!this.started) {
      this.started = true;
      this.send('start');
    } else if (this.ended) {
      this.ended = false;
      this.milestones.clear();
      this.send('restart');
    }
  }

  timeUpdate(seconds, percent) {
    const tick = this.lastSeconds === null ? 0 : seconds - this.lastSeconds;
    const watching = tick > 0 && tick < VIDEO_MAX_TICK_SECONDS;
    if (watching) this.unreportedSeconds += tick;
    this.lastSeconds = seconds;

    // A milestone a seek jumps over was not watched, so it is passed
    // without being reported.
    VIDEO_MILESTONES.forEach(milestone => {
      if (percent * 100 < milestone) return;
      if (this.milestones.has(milestone)) return;
      this.milestones.add(milestone);
      if (watching) this.send(String(milestone));
    });
  }

  // Players also pause at the end, which is reported as complete instead.
  pause(percent) {
    if (percent < 1) this.send('pause');
  }

  complete() {
    this.ended = true;
    this.send('complete');
  }

  // Each event carries the whole seconds watched since the previous one, so
  // Video Time Viewed sums correctly.
  send(type) {
    const secondsPlayed = Math.floor(this.unreportedSeconds);
    this.unreportedSeconds -= secondsPlayed;

    const detail = { eVar70: this.eVar70 };
    if (secondsPlayed > 0) detail.video = { secondsPlayed };
    fireAdobeAnalyticsEvent({ type: `video:${type}`, detail });
  }

  // IDs are case-sensitive and YouTube's carry hyphens, so only the category
  // and title go through the cleaner.
  get eVar70() {
    const { category, id, title } = this.getVideo();
    const cleaned = adobeCleanString(`${category}|${title}`);
    const pipe = cleaned.indexOf('|');
    return `${cleaned.slice(0, pipe)}|${id}|${cleaned.slice(pipe + 1)}`;
  }
}
